/*
  Flowkeeper - Pomodoro timer for power users and teams
  Copyright (c) 2023 Constantine Kulak, Geoffrey Espagne

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU General Public License as published by
  the Free Software Foundation; either version 3 of the License, or
  (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU General Public License for more details.

  You should have received a copy of the GNU General Public License
  along with this program.  If not, see <https://www.gnu.org/licenses/>.
*/

import React, {Fragment, useContext, useState} from 'react';
import {Alert, ListItem, ListVariant, LoginFooterItem, LoginForm, LoginPage} from '@patternfly/react-core';
import brandImg from '../../fk-logo-small.png';
import ExclamationCircleIcon from '@patternfly/react-icons/dist/esm/icons/exclamation-circle-icon';
import {GoogleFormLogin} from "../forms/GoogleFormLogin";
import {connection, initEventSource} from "../../fk/core/websocket_event_source";
import ConsentPage from "./ConsentPage";
import { GenericAlert } from '../misc/GenericAlert';

import DataContext from '../context/DataContext';
import ConnectionContext from '../context/ConnectionContext';

const Cookies = require("js-cookie");

function SignInPage() {
    const [showHelperText, setShowHelperText] = useState(false);
    const [username, setUsername] = useState('');
    const [isValidUsername, setIsValidUsername] = useState(true);
    const [password, setPassword] = useState('');
    const [isValidPassword, setIsValidPassword] = useState(true);
    const [error, setError] = React.useState('');

    const { data, setData } = useContext(DataContext);
    const { setConnection } = useContext(ConnectionContext);

    const onLoginButtonClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        setIsValidUsername(!!username);
        setIsValidPassword(!!password);
        setShowHelperText(!username || !password);
        Cookies.set('fw-uauthtype', 'basic', {expires: 30});
        Cookies.set('fw-uname', username, {expires: 30});
        Cookies.set('fw-utoken', password, {expires: 30});
        initEventSource(setData,
            setConnection,
            username,
            password,
            'basic',
            false);
    };

    return (<>
        {error && <Alert variant="warning" title='Login error' ouiaId="WarningAlert"><p>{error}</p></Alert>}
        {connection && connection.isLoginError && <GenericAlert variant="danger" truncateTitle={2} message={'Incorrect User/Password combination. Please retry.'} />}
        <LoginPage
            footerListVariants={ListVariant.inline}
            brandImgAlt="Flowkeeper logo"
            brandImgSrc={brandImg}
            backgroundImgSrc="/login-bg.png"
            footerListItems={<Fragment>
                <ListItem><LoginFooterItem href="https://www.patternfly.org/">Terms of Use </LoginFooterItem></ListItem>
                <ListItem><LoginFooterItem href="https://www.patternfly.org/">Help</LoginFooterItem></ListItem>
                <ListItem><LoginFooterItem href="https://www.patternfly.org/">Privacy Policy</LoginFooterItem></ListItem>
            </Fragment>}
            textContent="IMPORTANT: Flowkeeper.org is in its BETA testing stage. It lacks end-to-end encryption,
              which means that our engineers have access to your data. DO NOT STORE ANY SENSITIVE INFORMATION IN
              FLOWKEEPER.ORG -- we may read it or it may disappear at any time."
            loginTitle="Log in to your account"
            loginSubtitle="At the moment direct Sign Up is disabled. However you can sign in via Google to have your account created automatically."
            socialMediaLoginContent={<GoogleFormLogin errorCallback={setError} />}
            socialMediaLoginAriaLabel="Log in with social media"
            signUpForAccountMessage="You can only sign up via Google login.">
            <LoginForm
                showHelperText={showHelperText}
                helperText="Invalid login credentials."
                helperTextIcon={<ExclamationCircleIcon/>}
                usernameLabel="Email address"
                usernameValue={username}
                onChangeUsername={(_, value: string) => setUsername(value)}
                onChangePassword={(_, value: string) => setPassword(value)}
                isValidUsername={isValidUsername}
                passwordLabel="Password"
                passwordValue={password}
                isValidPassword={isValidPassword}
                onLoginButtonClick={onLoginButtonClick}
                loginButtonLabel="Sign in with password" />
            {data && data.needConsent && <ConsentPage />}
        </LoginPage>
    </>);
}

export default SignInPage;
